import { DEFAULT_WBS, migrateTaskWbs, workPackages } from "./wbs";
import type { WbsNode } from "./types";
import { derived, get, writable, type Writable } from "svelte/store";
import type { AuthState, CPMOutcome, MonteCarloResult, RiskEntry, Role, StatusRow, Task } from "./types";
import { PHASE_ALIASES } from "./types";
import { cloneSeedRisks, cloneSeedStatusRows, cloneSeedTasks } from "./seedData";
import { computeCPM, taskShiftsPerDay } from "./cpm";
import { parseIsoDate, projectDayLabel, scheduleFloorDay, todayIso } from "./projectDate";
import { runMonteCarlo } from "./montecarlo";
import * as pb from "./pocketbase";
import { DEFAULT_SUBCONTRACTOR_COLORS, SUBCONTRACTOR_OPTIONS } from "./subcontractors";
import { splitList } from "./board";
import type { BoardFilter, BoardSortKey } from "./board";
import { DEFAULT_PHASES } from "./phases";
import type { PhaseDef } from "./phases";
import type { Phase } from "./types";
import type { Shift } from "./shifts";

export type { BoardFilter };

export const wbsNodes = writable<WbsNode[]>(DEFAULT_WBS.map((n) => ({ ...n })));
export const tasks = writable<Task[]>(cloneSeedTasks());
export const risks = writable<RiskEntry[]>(cloneSeedRisks());
export const statusRows = writable<StatusRow[]>(cloneSeedStatusRows());
/** Whether the schedule may place work on Sat/Sun — off by default (weekends are non-working days, like Excel's WORKDAY). */
export const allowWeekendWork = writable<boolean>(false);
/**
 * Calendar date the changeover starts, "YYYY-MM-DD", or null for no calendar at all.
 * While it's null the board stays in pure relative-day mode and nothing auto-slides —
 * the schedule behaves exactly as it did before dates existed.
 */
export const projectStartDate = writable<string | null>(null);
/**
 * Civil date the plan was last snapshotted into every task's `baseline` (see
 * `setBaselineNow`), or null if that's never been done — in which case each task's
 * baseline is still whatever it was set to when the task was created.
 */
export const baselineSetAt = writable<string | null>(null);
/**
 * One color per subcontractor, shared project-wide (everyone should see "blue = ABB"
 * the same way) — unlike theme, which is a personal viewing preference. Defaults cover
 * the fixed `SUBCONTRACTOR_OPTIONS` set; a project can override any of them.
 */
export const subcontractorColors = writable<Record<string, string>>({ ...DEFAULT_SUBCONTRACTOR_COLORS });
/**
 * The pick-lists behind the edit modal's "add existing" dropdowns — explicit and
 * project-wide so an entry can be deleted for good (via `removeKnownSubcontractor` etc.),
 * not just left to disappear once nothing references it anymore.
 */
export const knownSubcontractors = writable<string[]>([...SUBCONTRACTOR_OPTIONS]);
export const knownEquipment = writable<string[]>([]);
export const knownPersonnel = writable<string[]>([]);
/**
 * "Today", as a civil date. A store rather than a direct clock read so the whole app
 * agrees on one value per session and tests can pin it.
 */
export const today = writable<string>(todayIso());
export const auth = writable<AuthState | null>(null);
export const activeTab = writable<"analysis" | "lean" | "wbs" | "risks" | "briefing" | "users">("lean");
export const toastMessage = writable<string>("");
export const toastUndo = writable<(() => void) | null>(null);
export const pbStatus = writable<string>(pb.AUTH_REQUIRED ? "" : "No persistence configured — changes are not saved.");
export const pbRecordId = writable<string | null>(null);
/** Every crane's planning, for the switcher — id + display name only. */
export const plannings = writable<pb.PlanningRecord[]>([]);
/** Which crane's planning is currently loaded. Null only before boot resolves one. */
export const currentPlanningId = writable<string | null>(null);
export const currentPlanning = derived([plannings, currentPlanningId], ([$plannings, $id]) =>
  $plannings.find((p) => p.id === $id) ?? null
);
/** Every RWG project, for the switcher — a project groups one or more cranes. */
export const projects = writable<pb.ProjectRecord[]>([]);
/** Which project is currently selected. Follows whichever crane is loaded; never set independently. */
export const currentProjectId = writable<string | null>(null);
export const currentProject = derived([projects, currentProjectId], ([$projects, $id]) =>
  $projects.find((p) => p.id === $id) ?? null
);
/** Whether the LEAN board groups this project's cards into phase swimlanes. Missing/undefined reads as on. */
export const useSwimLanes = derived(currentProject, ($p) => $p?.useSwimLanes ?? true);
/** Whether this project works two shifts a day. Missing/undefined reads as off. */
export const twoShifts = derived(currentProject, ($p) => $p?.twoShifts ?? false);
/** This project's own swimlanes, in display order. Missing/empty falls back to the app's original 4. */
export const projectPhases = derived(currentProject, ($p) => ($p?.phases?.length ? $p.phases : DEFAULT_PHASES));
export const iterations = writable<number>(2000);
export const monteCarloResult = writable<MonteCarloResult | null>(null);

/** Which Lean board view is showing. */
export const leanView = writable<"plan" | "week" | "today" | "summary">("plan");
/** Week the Week view is on, 0-based. Null follows the clock, like `selectedDay`. */
export const selectedWeek = writable<number | null>(null);

export const EMPTY_FILTER: BoardFilter = { text: "", critical: false, blocked: false, unfinished: false };
/**
 * What the board is looking for. Matches are highlighted and everything else dimmed
 * rather than hidden: on a board ten screens wide, losing the surrounding context is
 * what makes a result hard to place.
 */
export const boardFilter = writable<BoardFilter>({ ...EMPTY_FILTER });
export function clearBoardFilter() { boardFilter.set({ ...EMPTY_FILTER }); }
/** How cards within one cell are ordered — see `compareCards`. A view preference, not
 *  project data, so it isn't saved with the plan and resets to the default on reload. */
export const boardSortKey = writable<BoardSortKey>("schedule");
/**
 * The day the Today view is looking at. Null means "follow the clock" — it re-anchors
 * to the real today on every recalculation, which is what a board left running on a
 * site screen overnight needs to do. Stepping to another day pins it to a number.
 */
export const selectedDay = writable<number | null>(null);

/** The earliest day unfinished work may land on, or undefined with no calendar set. */
export const floorDay = derived(
  [projectStartDate, today],
  ([$projectStartDate, $today]): number | null => scheduleFloorDay($projectStartDate, $today)
);

export const det = derived(
  [tasks, allowWeekendWork, floorDay, twoShifts],
  ([$tasks, $allowWeekendWork, $floorDay, $twoShifts]): CPMOutcome =>
    computeCPM($tasks, "m", $allowWeekendWork, { floorDay: $floorDay ?? undefined, shiftsPerDay: $twoShifts ? 2 : 1 })
);

export const AUTH_REQUIRED = pb.AUTH_REQUIRED;

/**
 * Permissions.
 *
 * With no persistence server configured there's no sign-in and no roles at all, so
 * everything is allowed — that's the single-user, one-screen mode the app had before
 * accounts existed, and every check has to keep honouring it.
 *
 * Each rule is written once against an auth value, then exposed two ways: as a plain
 * predicate for the guards inside this module, and as a derived store for components,
 * which need it to re-evaluate when someone signs in, signs out, or their session
 * expires mid-edit.
 */
function roleOf(a: AuthState | null): Role {
  return a?.record.role ?? "viewer";
}
function editorOf(a: AuthState | null): boolean {
  return !pb.AUTH_REQUIRED || roleOf(a) === "editor" || roleOf(a) === "admin";
}
function adminOf(a: AuthState | null): boolean {
  return !pb.AUTH_REQUIRED || roleOf(a) === "admin";
}

export function currentRole(): Role {
  return roleOf(get(auth));
}
export function isEditor(): boolean {
  return editorOf(get(auth));
}
export function isAdmin(): boolean {
  return adminOf(get(auth));
}

/** May change the plan — what a component gates its inputs and drag targets on. */
export const canEdit = derived(auth, editorOf);
/** May manage users and create plannings. */
export const canAdmin = derived(auth, adminOf);
/** The signed-in user's role, for display. */
export const role = derived(auth, roleOf);

let toastTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Transient message, optionally with a way to take it back. An undo matters most for
 * actions whose effect isn't obvious from the gesture — dragging a sticky reads as
 * "I moved it", but it also pins the task, which is a constraint that outlives the drag.
 */
export function showToast(msg: string, undo?: () => void) {
  toastMessage.set(msg);
  toastUndo.set(undo ?? null);
  if (toastTimer) clearTimeout(toastTimer);
  // A little longer when there's something to click, since it has to be read and acted on.
  toastTimer = setTimeout(dismissToast, undo ? 7000 : 2600);
}

export function dismissToast() {
  toastMessage.set("");
  toastUndo.set(null);
  if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
}

export function runToastUndo() {
  const fn = get(toastUndo);
  dismissToast();
  fn?.();
}

/**
 * Keeps one in-memory list in step with its own PocketBase collection.
 *
 * Tasks and risks each live in their own collection (one record per item) so that two
 * people editing different tasks at the same time hit different records instead of
 * clobbering one shared blob. That means tracking, per app-level id, the PocketBase
 * record id needed to PATCH/DELETE it and a JSON snapshot of what was last written —
 * which is what lets a save touch only the records that actually changed.
 */
class RecordSync<T extends { id: string }> {
  private pbIds = new Map<string, string>();
  private lastSaved = new Map<string, string>();

  /** The collection is reached through a getter, not held directly, so that importing
   *  this module doesn't reach into the persistence layer before anything is saved —
   *  it isn't there at all in single-user mode. */
  constructor(private readonly api: () => pb.RecordApi<T>) {}

  /** Takes ownership of what a load just brought back: already saved, nothing to write. */
  adopt(entries: pb.RecordEntry<T>[]): T[] {
    this.pbIds = new Map(entries.map((e) => [e.value.id, e.pbId]));
    this.lastSaved = new Map(entries.map((e) => [e.value.id, JSON.stringify(e.value)]));
    return entries.map((e) => e.value);
  }

  /** Forgets everything — for a planning with no saved state, where nothing on screen
   *  corresponds to a record yet and every item is a create. */
  reset() {
    this.pbIds = new Map();
    this.lastSaved = new Map();
  }

  async push(authState: AuthState | null, planningId: string, current: T[]): Promise<void> {
    const currentIds = new Set(current.map((v) => v.id));
    for (const [id, pbId] of [...this.pbIds]) {
      if (currentIds.has(id)) continue;
      await this.api().remove(authState, pbId);
      this.pbIds.delete(id);
      this.lastSaved.delete(id);
    }
    for (const value of current) {
      const snapshot = JSON.stringify(value);
      const pbId = this.pbIds.get(value.id);
      if (!pbId) {
        this.pbIds.set(value.id, await this.api().create(authState, planningId, value));
      } else if (this.lastSaved.get(value.id) !== snapshot) {
        await this.api().update(authState, pbId, value);
      } else {
        continue;
      }
      this.lastSaved.set(value.id, snapshot);
    }
  }
}

const taskSync = new RecordSync<Task>(() => pb.taskRecords);
const riskSync = new RecordSync<RiskEntry>(() => pb.riskRecords);

let saveTimer: ReturnType<typeof setTimeout> | null = null;
/** Debounced save — call after any mutation to tasks, risks, or shared settings. No-ops for viewers and when no pb-url is configured. */
export function scheduleSave() {
  if (!pb.AUTH_REQUIRED) return; // no persistence configured; state is in-memory only, same as before
  if (!isEditor()) return;
  pbStatus.set("Saving…");
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      const authState = get(auth);
      const planningId = get(currentPlanningId);
      if (!planningId) return;
      const id = get(pbRecordId);
      const newId = await pb.saveAppState(authState, id, planningId, get(statusRows), {
        wbsNodes: get(wbsNodes),
        allowWeekendWork: get(allowWeekendWork),
        projectStartDate: get(projectStartDate),
        baselineSetAt: get(baselineSetAt),
        subcontractorColors: get(subcontractorColors),
        knownSubcontractors: get(knownSubcontractors),
        knownEquipment: get(knownEquipment),
        knownPersonnel: get(knownPersonnel),
      });
      pbRecordId.set(newId);
      await taskSync.push(authState, planningId, get(tasks));
      await riskSync.push(authState, planningId, get(risks));
      pbStatus.set("Saved.");
    } catch (e) {
      if (e instanceof pb.SessionExpiredError) {
        handleSessionExpired();
      } else {
        console.warn("Save failed:", e);
        pbStatus.set(e instanceof Error ? `Save failed — ${e.message}` : "Save failed — check the persistence server.");
      }
    }
  }, 800);
}

export function handleSessionExpired() {
  auth.set(null);
  pb.clearAuthStorage();
  pbStatus.set("Your session expired — sign in again.");
}

const CURRENT_PLANNING_KEY = "crane_current_planning";
function loadLastPlanningId(): string | null {
  try { return localStorage.getItem(CURRENT_PLANNING_KEY); } catch { return null; }
}
function saveLastPlanningId(id: string) {
  try { localStorage.setItem(CURRENT_PLANNING_KEY, id); } catch { /* ignore */ }
}

/** Fetches every project and makes sure at least one exists — a crane can't be seeded
 *  without one to belong to. If none exist yet at all, an admin's first boot seeds one
 *  named "RWG" so the app isn't stuck. */
async function resolveProjects(authState: AuthState | null): Promise<pb.ProjectRecord[]> {
  let list = await pb.listProjects(authState);
  if (!list.length) {
    if (!isAdmin()) { projects.set([]); return []; }
    list = [await pb.createProjectRecord(authState, "RWG")];
  }
  projects.set(list);
  return list;
}

/** Fetches every planning (one per crane, across every project) and settles on which
 *  one to show: the last one this browser used if it still exists, otherwise the first
 *  alphabetically. If none exist yet at all, an admin's first boot seeds one named
 *  "QC102" (the original single-crane planning this app was built around) under the
 *  first available project, so the app isn't stuck. Also sets the current project to
 *  whichever one the resolved crane belongs to. */
async function resolvePlannings(authState: AuthState | null): Promise<string | null> {
  let list = await pb.listPlannings(authState);
  if (!list.length) {
    if (!isAdmin()) return null;
    const projectId = get(projects)[0]?.id;
    if (!projectId) return null;
    list = [await pb.createPlanningRecord(authState, "QC102", projectId)];
  }
  plannings.set(list);
  const lastId = loadLastPlanningId();
  const resolved = list.find((p) => p.id === lastId) ?? list[0];
  currentPlanningId.set(resolved.id);
  // Legacy cranes saved before projects existed have no `project` yet — leave the
  // current project unresolved rather than pointing at nothing, until an admin
  // assigns one in PocketBase admin (see docs/PROJECTS-ROLLOUT.md).
  currentProjectId.set(resolved.project || null);
  saveLastPlanningId(resolved.id);
  return resolved.id;
}

/**
 * Full boot sequence: with no pb-url configured, resolves immediately (single-user,
 * no login, no plannings — same behaviour as before persistence/auth/multi-planning
 * existed). With one configured, tries the stored session first and only reports
 * needsLogin if that fails, then resolves which project and crane's planning to load.
 */
export async function bootSequence(): Promise<{ needsLogin: boolean }> {
  if (!pb.AUTH_REQUIRED) {
    await loadSavedState(null);
    return { needsLogin: false };
  }
  const restored = await pb.refreshAuth();
  if (!restored) return { needsLogin: true };
  auth.set(restored);
  // The projects feature is additive — a hiccup fetching/seeding it (e.g. the
  // `projects` collection not migrated onto this PocketBase yet) shouldn't block
  // loading cranes and their plans, which worked fine before projects existed.
  try {
    await resolveProjects(restored);
  } catch (e) {
    console.error("Failed to resolve projects", e);
  }
  const planningId = await resolvePlannings(restored);
  if (!planningId) {
    pbStatus.set("No crane planning exists yet — ask an admin to create one.");
    return { needsLogin: false };
  }
  await loadSavedState(planningId);
  return { needsLogin: false };
}

/** Switches to a different crane's planning: persists the choice, clears the
 *  current in-memory board, and reloads (or seeds LEAN defaults for a brand-new one). */
export async function switchPlanning(planningId: string): Promise<void> {
  if (get(currentPlanningId) === planningId) return;
  const rec = get(plannings).find((p) => p.id === planningId);
  currentPlanningId.set(planningId);
  if (rec) currentProjectId.set(rec.project);
  saveLastPlanningId(planningId);
  await loadSavedState(planningId);
}

/**
 * Adds a new crane to the current project and switches to it, then starts it either
 * empty or with `importedTasks` (e.g. from a parsed P6 XML file — see `p6xmlImport.ts`).
 * Admin-only.
 *
 * `switchPlanning` loads this brand-new planning's (nonexistent) saved state, which
 * falls back to `resetToDefaults()` — appropriate for that fallback in general, but
 * not for a crane someone just deliberately created, so its result is overwritten
 * here rather than left as the QC102 demo dataset.
 */
export async function createPlanning(name: string, importedTasks: Task[] | null = null): Promise<void> {
  if (!isAdmin()) return;
  const trimmed = name.trim();
  if (!trimmed) return;
  const projectId = get(currentProjectId);
  if (!projectId) return;
  const authState = get(auth);
  const rec = await pb.createPlanningRecord(authState, trimmed, projectId);
  plannings.update((list) => [...list, rec].sort((a, b) => a.name.localeCompare(b.name)));
  await switchPlanning(rec.id);
  tasks.set(importedTasks ?? []);
  scheduleSave();
}

/** Switches to a different project: selects its first crane (alphabetically), or
 *  leaves the board as-is with a status message if the project has none yet. */
export async function switchProject(projectId: string): Promise<void> {
  if (get(currentProjectId) === projectId) return;
  currentProjectId.set(projectId);
  const firstCrane = get(plannings)
    .filter((p) => p.project === projectId)
    .sort((a, b) => a.name.localeCompare(b.name))[0];
  if (firstCrane) {
    await switchPlanning(firstCrane.id);
  } else {
    pbStatus.set("This project has no crane yet — add one to start planning.");
  }
}

/** Adds a new project and switches to it. Admin-only. Doesn't create a crane —
 *  use "+ Add crane" for that once the project is selected. */
export async function createProject(name: string): Promise<void> {
  if (!isAdmin()) return;
  const trimmed = name.trim();
  if (!trimmed) return;
  const authState = get(auth);
  const rec = await pb.createProjectRecord(authState, trimmed);
  projects.update((list) => [...list, rec].sort((a, b) => a.name.localeCompare(b.name)));
  currentProjectId.set(rec.id);
}

/** Toggles whether the LEAN board uses phase swimlanes for a project. Admin-only. */
export async function updateProjectSwimLanes(projectId: string, value: boolean): Promise<void> {
  if (!isAdmin()) return;
  const authState = get(auth);
  const rec = await pb.updateProjectRecord(authState, projectId, { useSwimLanes: value });
  projects.update((list) => list.map((p) => (p.id === projectId ? rec : p)));
}

/** Toggles whether a project works two shifts a day. Admin-only. */
export async function updateProjectTwoShifts(projectId: string, value: boolean): Promise<void> {
  if (!isAdmin()) return;
  const authState = get(auth);
  const rec = await pb.updateProjectRecord(authState, projectId, { twoShifts: value });
  projects.update((list) => list.map((p) => (p.id === projectId ? rec : p)));
}

/** Replaces a project's whole swimlane list — add/rename/remove/reorder are all just
 *  "save this new array" from the editor. Admin-only. */
export async function updateProjectPhases(projectId: string, phases: PhaseDef[]): Promise<void> {
  if (!isAdmin()) return;
  const authState = get(auth);
  const rec = await pb.updateProjectRecord(authState, projectId, { phases });
  projects.update((list) => list.map((p) => (p.id === projectId ? rec : p)));
}

// Saved state is an opaque JSON blob in PocketBase with no schema migration of its
// own — records saved before a field existed come back without it. Backfill
// defaults here so older saved tasks don't crash code that assumes these are
// always present (e.g. `t.subcontractors.includes(...)`).
export function normalizeTask(t: Task): Task {
  return {
    ...migrateTaskWbs(t),
    // "Commisioning" was the stored key until the spelling was fixed; saved records
    // still carry it.
    phase: PHASE_ALIASES[t.phase as string] ?? t.phase,
    subcontractors: t.subcontractors ?? [],
    responsibleParty: t.responsibleParty ?? "",
    requiredEquipment: t.requiredEquipment ?? "",
    requiredPersonnel: t.requiredPersonnel ?? "",
    doneDays: t.doneDays ?? [],
    blocked: t.blocked ?? false,
    blockedReason: t.blockedReason ?? "",
    blockedSince: t.blockedSince ?? null,
    weekendWork: t.weekendWork ?? "auto",
    doubleShift: t.doubleShift ?? false,
  };
}

/**
 * Status rows are a fixed set now (see STATUS_ROW_SEED) — label, on/off wording and
 * `normal` always come from the current seed, matched to a saved record by id. Only
 * `values` (the actual day-by-day plan) is real saved data; anything else in the
 * record (a stale label, a since-removed custom row from before rows were locked
 * down) is discarded rather than trusted.
 */
function normalizeStatusRows(loaded: StatusRow[] | undefined): StatusRow[] {
  return cloneSeedStatusRows().map((seedRow) => ({
    ...seedRow,
    values: loaded?.find((r) => r.id === seedRow.id)?.values ?? [],
  }));
}

/** Resets the board to the same LEAN-planning defaults it starts from on a fresh
 *  install — used for a planning that doesn't have an app_state record yet, whether
 *  that's the very first boot or switching to a newly-created crane. */
function resetToDefaults() {
  pbRecordId.set(null);
  wbsNodes.set(DEFAULT_WBS.map((n) => ({ ...n })));
  statusRows.set(cloneSeedStatusRows());
  allowWeekendWork.set(false);
  projectStartDate.set(null);
  baselineSetAt.set(null);
  subcontractorColors.set({ ...DEFAULT_SUBCONTRACTOR_COLORS });
  tasks.set(cloneSeedTasks());
  knownSubcontractors.set([...SUBCONTRACTOR_OPTIONS]);
  knownEquipment.set([]);
  knownPersonnel.set([]);
  risks.set(cloneSeedRisks());
  taskSync.reset();
  riskSync.reset();
}

async function loadSavedState(planningId: string | null) {
  if (!pb.AUTH_REQUIRED) return;
  if (!planningId) return;
  pbStatus.set("Loading saved state…");
  try {
    const authState = get(auth);
    const rec = await pb.loadAppState(authState, planningId);
    if (rec) {
      pbRecordId.set(rec.id);
      wbsNodes.set(rec.wbsNodes ?? DEFAULT_WBS.map((n) => ({ ...n })));
      statusRows.set(normalizeStatusRows(rec.statusRows));
      allowWeekendWork.set(rec.allowWeekendWork ?? false);
      projectStartDate.set(rec.projectStartDate ?? null);
      baselineSetAt.set(rec.baselineSetAt ?? null);
      subcontractorColors.set({ ...DEFAULT_SUBCONTRACTOR_COLORS, ...(rec.subcontractorColors ?? {}) });

      const loadedTasks = taskSync.adopt(await pb.taskRecords.list(authState, planningId)).map(normalizeTask);
      tasks.set(loadedTasks);

      // Records saved before these pick-lists existed come back without them — backfill
      // from whatever's already in use so nothing already on a task vanishes from the
      // dropdown. Once saved once, an explicit (even empty) list always wins.
      knownSubcontractors.set(rec.knownSubcontractors ?? [
        ...new Set([...SUBCONTRACTOR_OPTIONS, ...loadedTasks.flatMap((t) => t.subcontractors), ...Object.keys(rec.subcontractorColors ?? {})]),
      ]);
      knownEquipment.set(rec.knownEquipment ?? [...new Set(loadedTasks.flatMap((t) => splitList(t.requiredEquipment)))]);
      knownPersonnel.set(rec.knownPersonnel ?? [...new Set(loadedTasks.flatMap((t) => splitList(t.requiredPersonnel)))]);

      risks.set(riskSync.adopt(await pb.riskRecords.list(authState, planningId)));

      pbStatus.set("Loaded saved state.");
    } else {
      resetToDefaults();
      pbStatus.set("No saved state yet — starting from LEAN planning defaults.");
    }
  } catch (e) {
    if (e instanceof pb.SessionExpiredError) {
      handleSessionExpired();
    } else {
      console.warn("Load failed, starting from LEAN planning defaults:", e);
      pbStatus.set("Couldn't reach the persistence server — changes won't be saved.");
    }
  }
}

export async function doLogin(email: string, password: string): Promise<void> {
  const a = await pb.login(email, password);
  pb.saveAuthToStorage(a);
  auth.set(a);
  const planningId = await resolvePlannings(a);
  if (!planningId) {
    pbStatus.set("No crane planning exists yet — ask an admin to create one.");
    return;
  }
  await loadSavedState(planningId);
}

export function doSignOut() {
  auth.set(null);
  pb.clearAuthStorage();
  location.reload();
}

/** Update names/codes without changing stable activity references. */
export function saveWbsNode(node: WbsNode): string {
  if (!isEditor()) return "Editing permission required.";
  const nodes = get(wbsNodes);
  const parent = nodes.find((n) => n.id === node.parentId);
  if (node.id === "wbs-1" || (parent && parent.id !== "wbs-1" && parent.parentId !== "wbs-1")) return "Choose the project or a scope as parent.";
  if (!parent || !node.name.trim()) return "Choose a parent and enter a name.";
  if (!/^\d+(\.\d+)*$/.test(node.code) || !node.code.startsWith(parent.code + ".") || node.code.split(".").length !== parent.code.split(".").length + 1)
    return "Use the parent code followed by one number (for example 1.4.5).";
  if (nodes.some((n) => n.id !== node.id && n.code === node.code)) return "This WBS code already exists.";
  const existing = nodes.find((n) => n.id === node.id);
  if (existing && existing.parentId !== node.parentId) return "The parent of an existing WBS item cannot be changed.";
  if (get(tasks).some((t) => t.wbsId === parent.id)) return "A work package with activities cannot become a parent scope.";
  const descendants = existing ? nodes.filter((n) => n.code.startsWith(existing.code + ".")) : [];
  const changed = { ...node, name: node.name.trim() };
  const updated = nodes.map((n) => n.id === node.id ? changed : descendants.some((d) => d.id === n.id)
    ? { ...n, code: node.code + n.code.slice(existing!.code.length) } : n);
  if (!existing) updated.push(changed);
  if (new Set(updated.map((n) => n.code)).size !== updated.length) return "This change would duplicate a WBS code.";
  wbsNodes.set(updated);
  scheduleSave();
  return "";
}

// ---- Edit modal ----
export const editingTaskId = writable<string | null>(null);
export const newTaskDraft = writable<Task | null>(null);
export function openAddActivity(wbsId: string | null = null) {
  if (!isEditor()) return;
  editingTaskId.set(null);
  newTaskDraft.set({
    id: "", wbsId, name: "", type: "activity", phase: get(projectPhases)[0]?.name ?? "", planDay: "custom",
    baseline: 1, o: 1, m: 1, p: 1, risk: "", preds: [], pinnedDay: null, weekendWork: "auto", doubleShift: false,
    subcontractors: [], responsibleParty: "", requiredEquipment: "", requiredPersonnel: "",
    doneDays: [], blocked: false, blockedReason: "", blockedSince: null, requiredStatus: [],
    actualStart: null, actualFinish: null,
  });
}
export function createTask(draft: Task): string | null {
  if (!isEditor() || !draft.name.trim()) return null;
  if (draft.wbsId && !workPackages(get(wbsNodes)).some((n) => n.id === draft.wbsId)) return null;
  const list = get(tasks);
  const id = crypto.randomUUID();
  tasks.set([...list, { ...draft, id, name: draft.name.trim(), baseline: draft.m }]);
  scheduleSave();
  return id;
}
export function openEditModal(id: string) { newTaskDraft.set(null); editingTaskId.set(id); }
export function closeEditModal() { editingTaskId.set(null); newTaskDraft.set(null); }

// ---- Own account (profile) modal ----
export const profileModalOpen = writable<boolean>(false);
export function openProfileModal() { profileModalOpen.set(true); }
export function closeProfileModal() { profileModalOpen.set(false); }

// ---- Manage swimlanes modal ----
export const phasesModalOpen = writable<boolean>(false);
export function openPhasesModal() { phasesModalOpen.set(true); }
export function closePhasesModal() { phasesModalOpen.set(false); }

export async function saveProfile(patch: { name?: string; avatarFile?: File | null; removeAvatar?: boolean }): Promise<void> {
  const current = get(auth);
  if (!current) return;
  try {
    const updated = await pb.updateProfile(current, patch);
    const next = { ...current, record: { ...current.record, ...updated } };
    auth.set(next);
    pb.saveAuthToStorage(next);
    showToast("Profile updated");
  } catch (e) {
    if (e instanceof pb.SessionExpiredError) { handleSessionExpired(); return; }
    showToast(e instanceof Error ? e.message : "Couldn't update profile.");
    throw e;
  }
}

export async function changeOwnPassword(oldPassword: string, newPassword: string): Promise<void> {
  const current = get(auth);
  if (!current) return;
  try {
    await pb.changeOwnPassword(current, oldPassword, newPassword);
    showToast("Password changed");
  } catch (e) {
    if (e instanceof pb.SessionExpiredError) { handleSessionExpired(); return; }
    showToast(e instanceof Error ? e.message : "Couldn't change password.");
    throw e;
  }
}

// ---- Monte Carlo ----
export function runMonteCarloNow(): void {
  const d = get(det);
  if (!d.ok) return;
  const n = Math.max(200, Math.min(20000, Math.floor(get(iterations)) || 2000));
  monteCarloResult.set(runMonteCarlo(get(tasks), n, get(allowWeekendWork), get(twoShifts) ? 2 : 1));
}

// ---- Weekend work rule (global, affects the whole schedule) ----
export function setAllowWeekendWork(value: boolean) {
  if (!isEditor()) return;
  allowWeekendWork.set(value);
  scheduleSave();
}

/**
 * Set (or clear, with "") the changeover's calendar start date. Clearing it puts the
 * board back into relative-day mode: no dates, and no automatic sliding of late work.
 */
export function setProjectStartDate(value: string) {
  if (!isEditor()) return;
  const trimmed = value.trim();
  if (trimmed === "") {
    projectStartDate.set(null);
  } else {
    if (!parseIsoDate(trimmed)) return;
    projectStartDate.set(trimmed);
  }
  scheduleSave();
}

/**
 * Snapshots every activity's current estimate (`m`) into its `baseline`, and stamps
 * `baselineSetAt` with today's date. Meant to be called right before execution starts,
 * so `baselineSchedule()` (see summary.ts) reports drift against the plan as it stood at
 * kickoff rather than whatever each task's estimate happened to be when it was first
 * added to the board.
 */
export function setBaselineNow() {
  if (!isEditor()) return;
  const previousTasks = get(tasks);
  const previousStamp = get(baselineSetAt);
  tasks.set(previousTasks.map((t) => ({ ...t, baseline: Math.max(0, Number(t.m) || 0) })));
  baselineSetAt.set(get(today));
  scheduleSave();
  showToast("Baseline set to the current plan", () => {
    tasks.set(previousTasks);
    baselineSetAt.set(previousStamp);
    scheduleSave();
  });
}

/** Subcontractor color assignments (shared, project-wide — see `subcontractorColors` above). */
export function setSubcontractorColor(name: string, hex: string) {
  if (!isEditor()) return;
  subcontractorColors.update((colors) => ({ ...colors, [name]: hex }));
  scheduleSave();
}

/**
 * Adds to / permanently removes from one of the edit modal's pick-lists
 * (`knownSubcontractors`/`knownEquipment`/`knownPersonnel`). Removing only hides an
 * entry from the "add existing" dropdown going forward — it doesn't touch any task
 * that already has it assigned.
 */
function addKnownValue(list: Writable<string[]>, name: string) {
  const trimmed = name.trim();
  if (!isEditor() || !trimmed) return;
  list.update((names) => (names.includes(trimmed) ? names : [...names, trimmed]));
  scheduleSave();
}
function removeKnownValue(list: Writable<string[]>, name: string) {
  if (!isEditor()) return;
  list.update((names) => names.filter((n) => n !== name));
  scheduleSave();
}

export const addKnownSubcontractor = (name: string) => addKnownValue(knownSubcontractors, name);
export const removeKnownSubcontractor = (name: string) => removeKnownValue(knownSubcontractors, name);
export const addKnownEquipment = (name: string) => addKnownValue(knownEquipment, name);
export const removeKnownEquipment = (name: string) => removeKnownValue(knownEquipment, name);
export const addKnownPersonnel = (name: string) => addKnownValue(knownPersonnel, name);
export const removeKnownPersonnel = (name: string) => removeKnownValue(knownPersonnel, name);

// ---- Progress (per-day done marking, and blocking) ----

/**
 * Tick this task's work off for one day, or un-tick it. Each marked day counts against
 * the planned duration, so the schedule places one day less of remaining work and the
 * rest resumes from the following day.
 */
export function toggleTaskDayDone(taskId: string, day: number) {
  if (!isEditor()) return;
  tasks.update((list) =>
    list.map((t) => {
      if (t.id !== taskId) return t;
      const done = new Set(t.doneDays ?? []);
      if (done.has(day)) done.delete(day);
      else done.add(day);
      // Finishing the last outstanding day resolves any block by definition — work
      // that's done isn't waiting on anything, and leaving both flags set showed a
      // green card with an "Unblock" button on it.
      const shiftsPerDay = taskShiftsPerDay(t, get(twoShifts) ? 2 : 1);
      const duration = t.type === "milestone" ? 0 : Math.max(0, Number(t.m) || 0) / shiftsPerDay;
      const complete = done.size > 0 && done.size >= duration;
      return {
        ...t,
        doneDays: [...done].sort((a, b) => a - b),
        blocked: complete ? false : t.blocked,
        blockedReason: complete ? "" : t.blockedReason,
      };
    })
  );
  scheduleSave();
}

/**
 * Flag work as blocked, with the reason. This doesn't move the schedule on its own —
 * unfinished work already slides to today — it records why it keeps sliding, which is
 * what the standup and the reporting readout need to see.
 */
export function setTaskBlocked(taskId: string, blocked: boolean, reason = "") {
  if (!isEditor()) return;
  const today = get(floorDay);
  tasks.update((list) =>
    list.map((t) => {
      if (t.id !== taskId) return t;
      if (!blocked) return { ...t, blocked: false, blockedReason: "", blockedSince: null };
      return {
        ...t,
        blocked: true,
        blockedReason: reason || t.blockedReason,
        // Re-blocking something already blocked keeps the original date, so the age
        // reported in the summary is how long it's really been standing.
        blockedSince: t.blocked && t.blockedSince != null ? t.blockedSince : today,
      };
    })
  );
  scheduleSave();
}

export function setTaskBlockedReason(taskId: string, reason: string) {
  if (!isEditor()) return;
  tasks.update((list) => list.map((t) => (t.id === taskId ? { ...t, blockedReason: reason } : t)));
  scheduleSave();
}

// ---- Rescheduling a card on the Lean board ----
//
// Dragging a sticky, the Alt+arrow keyboard equivalent, and dropping one card onto
// another are three gestures with one effect: the task moves, and it gets *pinned* to
// where it was put. Each of the three board views offers some subset of them, so the
// effect lives here rather than being reimplemented per view.

/** "Wed 16 Sep", or "day 3" before a start date is set — toasts read mid-sentence. */
function dayLabel(day: number): string {
  return projectDayLabel(get(projectStartDate), day, "day");
}

/**
 * Moving reads as "I put it here", but it also sets a pin — a constraint that outlives
 * the gesture and keeps applying on every later recalculation. Offering the previous
 * state back through the toast is what keeps the edit modal from being the only way
 * out of an accidental drag.
 *
 * Only the one task is put back, not the whole list as it was: the toast stands for
 * seconds, and whatever else happened on the board in the meantime — a day ticked off,
 * something else blocked — was nobody's idea of a thing to undo.
 */
function restoreTask(before: Task) {
  tasks.update((list) => list.map((t) => (t.id === before.id ? before : t)));
  scheduleSave();
  showToast(`${before.id} restored`);
}

function pinToast(id: string, day: number, phase: Phase, phaseChanged: boolean, before: Task) {
  showToast(
    `${id} pinned to ${dayLabel(day)}` + (phaseChanged ? ` · ${phase}` : ""),
    () => restoreTask(before)
  );
}

/** Reschedules one task onto `day` in `phase` — what a drop onto a board cell does. An
 *  omitted `shift` leaves the task's current shift alone, e.g. a drop in a project that
 *  doesn't split its board into shift columns. */
export function pinTaskTo(id: string, day: number, phase: Phase, shift?: Shift) {
  if (!isEditor()) return;
  const before = get(tasks).find((t) => t.id === id);
  if (!before) return;
  const pinnedShift = shift ?? before.pinnedShift ?? null;
  tasks.update((list) => list.map((t) => (t.id === id ? { ...t, phase, pinnedDay: day, pinnedShift } : t)));
  scheduleSave();
  pinToast(id, day, phase, before.phase !== phase, before);
}

/**
 * The keyboard equivalent of the drag: shift a task by days, across phase lanes, and/or
 * between shifts. Moves from where the task actually sits rather than from its pin, so
 * the first press does something visible even when nothing is pinned yet.
 */
export function moveTaskBy(id: string, dayDelta: number, phaseDelta: number, shiftDelta = 0) {
  if (!isEditor()) return;
  const result = get(det);
  const task = get(tasks).find((t) => t.id === id);
  if (!task || !result.ok) return;
  const currentDay = Math.max(0, Math.floor((result.ES[id] ?? 0) + 1e-9));
  const day = Math.max(0, currentDay + dayDelta);
  const phaseNames = get(projectPhases).map((p) => p.name);
  const phaseIdx = phaseNames.indexOf(task.phase);
  const phase = phaseIdx === -1 ? task.phase : phaseNames[Math.min(phaseNames.length - 1, Math.max(0, phaseIdx + phaseDelta))];
  const currentShift = task.pinnedShift ?? 1;
  const shift = shiftDelta ? (Math.min(2, Math.max(1, currentShift + shiftDelta)) as Shift) : undefined;
  if (day === task.pinnedDay && phase === task.phase && !shiftDelta) return;
  pinTaskTo(id, day, phase, shift);
}

/** Drops the pin, handing the task back to automatic scheduling. */
export function unpinTask(id: string) {
  if (!isEditor()) return;
  const before = get(tasks).find((t) => t.id === id);
  if (!before) return;
  tasks.update((list) => list.map((t) => (t.id === id ? { ...t, pinnedDay: null } : t)));
  scheduleSave();
  showToast(`${id} unpinned — scheduling automatically again`, () => restoreTask(before));
}

/**
 * Dropping a card directly onto another one reschedules it to that cell (same as
 * dropping on the cell background) and also positions it right after the target in the
 * underlying task order — which is what same-day stacking within a cell falls back to
 * when start times tie.
 */
export function reorderTaskAfter(draggedId: string, targetId: string, day: number, phase: Phase, shift?: Shift) {
  if (!isEditor()) return;
  const before = get(tasks);
  const dragged = before.find((t) => t.id === draggedId);
  if (!dragged || !before.some((t) => t.id === targetId)) return;
  const phaseChanged = dragged.phase !== phase;
  const pinnedShift = shift ?? dragged.pinnedShift ?? null;
  const without = before.filter((t) => t.id !== draggedId);
  without.splice(without.findIndex((t) => t.id === targetId) + 1, 0, { ...dragged, phase, pinnedDay: day, pinnedShift });
  tasks.set(without);
  scheduleSave();
  // Unlike a pin, this rewrote the list's order as well as the task, so undo has to put
  // the whole list back rather than just the one record.
  showToast(
    `${draggedId} moved after ${targetId} on ${dayLabel(day)}` + (phaseChanged ? ` · ${phase}` : ""),
    () => { tasks.set(before); scheduleSave(); showToast(`${draggedId} restored`); }
  );
}

// ---- Bulk edit (select several cards at once, act on all of them together) ----

/** Off by default — a click on a card opens it, same as always. Turning this on is what
 *  repurposes a click into a selection toggle instead, across every board view. */
export const bulkEditMode = writable(false);
/** Task ids currently selected. Cleared whenever bulk edit mode is turned off, so
 *  leaving and re-entering it never carries a stale selection forward. */
export const bulkSelection = writable<Set<string>>(new Set());

export function setBulkEditMode(on: boolean) {
  bulkEditMode.set(on);
  bulkSelection.set(new Set());
}

export function toggleBulkSelect(id: string) {
  bulkSelection.update((sel) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}

export function clearBulkSelection() {
  bulkSelection.set(new Set());
}

/**
 * Moves every selected task into `phase`, leaving each one's day, pin and shift exactly
 * as they were — the bulk equivalent of dragging a card sideways into a different
 * swimlane without touching which day it's on. `phase` is display grouping only (the
 * schedule itself never looks at it), so this can't move the end date.
 */
export function bulkSetPhase(ids: string[], phase: Phase) {
  if (!isEditor() || !ids.length) return;
  const before = get(tasks);
  const idSet = new Set(ids);
  const changedCount = before.filter((t) => idSet.has(t.id) && t.phase !== phase).length;
  if (!changedCount) return;
  tasks.update((list) => list.map((t) => (idSet.has(t.id) ? { ...t, phase } : t)));
  scheduleSave();
  showToast(
    `${changedCount} ${changedCount === 1 ? "activity" : "activities"} moved to ${phase}`,
    () => { tasks.set(before); scheduleSave(); showToast(`${changedCount === 1 ? "Activity" : "Activities"} restored`); }
  );
}

/**
 * Shifts every selected task by the same number of days, each from wherever it actually
 * sits right now (not from its pin) — the bulk equivalent of Alt+←/→ on one card at a
 * time. Pins every task touched, same as a single drag does.
 */
export function bulkShiftDay(ids: string[], dayDelta: number) {
  if (!isEditor() || !ids.length || !dayDelta) return;
  const result = get(det);
  if (!result.ok) return;
  const before = get(tasks);
  const idSet = new Set(ids);
  const next = before.map((t) => {
    if (!idSet.has(t.id)) return t;
    const currentDay = Math.max(0, Math.floor((result.ES[t.id] ?? 0) + 1e-9));
    const day = Math.max(0, currentDay + dayDelta);
    return { ...t, pinnedDay: day, pinnedShift: t.pinnedShift ?? null };
  });
  tasks.set(next);
  scheduleSave();
  showToast(
    `${ids.length} ${ids.length === 1 ? "activity" : "activities"} moved ${dayDelta > 0 ? "later" : "earlier"} by ${Math.abs(dayDelta)} day${Math.abs(dayDelta) === 1 ? "" : "s"}`,
    () => { tasks.set(before); scheduleSave(); showToast(`${ids.length === 1 ? "Activity" : "Activities"} restored`); }
  );
}

/** Drops the pin on every selected task, handing all of them back to automatic scheduling. */
export function bulkUnpin(ids: string[]) {
  if (!isEditor() || !ids.length) return;
  const before = get(tasks);
  const idSet = new Set(ids);
  const pinnedCount = before.filter((t) => idSet.has(t.id) && t.pinnedDay != null).length;
  if (!pinnedCount) return;
  tasks.update((list) => list.map((t) => (idSet.has(t.id) ? { ...t, pinnedDay: null } : t)));
  scheduleSave();
  showToast(
    `${pinnedCount} ${pinnedCount === 1 ? "activity" : "activities"} unpinned — scheduling automatically again`,
    () => { tasks.set(before); scheduleSave(); showToast(`${pinnedCount === 1 ? "Activity" : "Activities"} restored`); }
  );
}

// ---- Status rows (Lean board day-by-day site status, e.g. trafo power) ----
/**
 * Cycles one day of a status row: not set → on → off → not set.
 *
 * Three states rather than two because these rows are the *plan* for site conditions.
 * "The trafo is planned off on Thursday" and "nobody has decided about Thursday yet"
 * are different facts, and collapsing them meant an untouched cell read as a decision.
 *
 * `suggestion` is whatever a scheduled card's requirement already shows on an
 * undecided day (see requiredRowValue) — when there is one, the cycle enters through
 * it first (confirm the suggestion, then its opposite, then back to not-set) instead
 * of always starting at "on" regardless of what the cell was already displaying.
 */
export function cycleStatusDay(rowId: string, day: number, suggestion: boolean | null = null) {
  if (!isEditor()) return;
  statusRows.update((rows) =>
    rows.map((r) => {
      if (r.id !== rowId) return r;
      const values = [...r.values];
      const current = values[day] ?? null;
      const order: (boolean | null)[] = suggestion != null ? [suggestion, !suggestion, null] : [true, false, null];
      values[day] = order[(order.indexOf(current) + 1) % order.length];
      return { ...r, values };
    })
  );
  scheduleSave();
}


export function resetTasksToSeed() {
  tasks.set(cloneSeedTasks());
  scheduleSave();
}
export function resetRisksToSeed() {
  risks.set(cloneSeedRisks());
  scheduleSave();
}

let nextRiskN = 1;
export function addRisk(fields: Partial<RiskEntry> = {}): RiskEntry {
  let id = fields.id?.trim() || "";
  if (!id) {
    do { id = "R-NEW" + nextRiskN++; } while (get(risks).some((r) => r.id === id));
  }
  const entry: RiskEntry = { desc: "", probability: "", impact: "", owner: "", mitigation: "", status: "Needs input", ...fields, id };
  risks.update((list) => [...list, entry]);
  scheduleSave();
  return entry;
}
export function updateRisk(id: string, patch: Partial<RiskEntry>) {
  risks.update((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  scheduleSave();
}
export function deleteRisk(id: string) {
  risks.update((list) => list.filter((r) => r.id !== id));
  scheduleSave();
}
